import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  makeStyles,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import EditIcon from "@material-ui/icons/Edit";

import { appUrl } from "../../../../../utils/config";
import axios from "axios";
import {
  deriveTable2Options,
  tabBadgeCount,
  cascadeRemoveSloc,
  SLOC_ENDPOINTS,
  isBlankSloc,
  planSlocDelete,
} from "./plantSlocLogic";
import DeletePlantDrawer from "./ModalPopUps/DeletePlantDrawer";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
    gap: "32px",
  },
  sectionTitle: {
    fontWeight: 600,
    color: "#333",
    marginBottom: theme.spacing(1),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "1000px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  iconBtn: {
    padding: "4px",
    color: "#000",
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 8,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  editIcon: {
    color: "#f26522",
  },
  errorBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60px",
    backgroundColor: "#ffecec",
    color: "#b00020",
    border: "1px solid #ff9999",
  },
  rowError: {
    color: "red",
    fontSize: "0.75rem",
    marginTop: "4px",
  },
  // BCM-234 A6: blue clickable deep-link for the Associated Products count.
  productLink: {
    color: "#286ACF",
    textDecoration: "underline",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const buildUrl = (endpoint) =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/${endpoint}`;

const PlantSLocTab = (props) => {
  const { featuresAccess, sponsorId, onPlantRowsChange } = props;
  const classes = useStyles();
  const history = useHistory();

  // BCM-234 A6 deep-link: open the GLOBAL (no-CMO) Product Details dashboard
  // pre-filtered by this Plant ID + Status=Active. Mirrors the navigation pattern
  // used by CmosNetworkDashboard.handleClickView, but targets the bare /products
  // route (no :vendorMosymphonyID) so the dashboard is NOT scoped to any one CMO.
  const openProductsForPlant = (plantID) => {
    history.push({
      pathname: `/pcmp/Pharma Co/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/products`,
      state: {
        productPreFilters: { plantID: String(plantID), status: "Active" },
      },
    });
  };

  // ---- Table 1: Storage Location ID master ----
  const [storageLocations, setStorageLocations] = useState([]);
  const [loadingT1, setLoadingT1] = useState(false);
  const [errorT1, setErrorT1] = useState(null);
  const [savingT1, setSavingT1] = useState({});

  // ---- Table 2: Plant -> Associated SLOCs ----
  const [plantRows, setPlantRows] = useState([]);
  const [loadingT2, setLoadingT2] = useState(false);
  const [errorT2, setErrorT2] = useState(null);
  const [savingT2, setSavingT2] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);

  // BCM-234 A7: plantID whose Delete-Plant drawer is open (null = closed).
  const [deletePlantTarget, setDeletePlantTarget] = useState(null);

  // BCM-234 A6: active-product count per Plant ID (across all CMOs), keyed by plantID.
  const [productCounts, setProductCounts] = useState({});

  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;
  const ecosystemId = localStorage.getItem("ecosystemId");

  // Options for Table 2 multi-select are DERIVED FROM Table 1 only (Req 11.5 / Property 8)
  const slocOptions = deriveTable2Options(
    storageLocations.map((row) => row.storageLocationID)
  );

  // Keep the parent tab badge in sync with the plant row count (Req 11.3, 11.4 / Property 9)
  useEffect(() => {
    if (typeof onPlantRowsChange === "function") {
      onPlantRowsChange(plantRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantRows.length]);

  // ================= Table 1 — load =================
  // Note: sponsorId / ecosystemId are intentionally NOT sent in any SLoc-tab
  // request (read or write, Table 1 or Table 2). The MW resolves the tenant
  // from the auth token (resolveTenant + brandOwnerMosymphonyID stamping);
  // sending them from the FE is dead-weight.
  const fetchStorageLocations = async () => {
    setErrorT1(null);
    setLoadingT1(true);
    try {
      const response = await axios.post(
        buildUrl(SLOC_ENDPOINTS.table1.load),
        {}
      );
      const data = response?.data?.data ?? response?.data;
      // Response shape (new contract): { storageLocationMasterList: [{ storageLocationMasterID, storageLocationID }] }
      // Old keys kept as fallback only.
      const list =
        data?.storageLocationMasterList ||
        data?.storageLocationList ||
        data?.storageLocationsList;
      if (Array.isArray(list)) {
        const formatted = list.map((item) => ({
          id: item.storageLocationMasterID,
          storageLocationID: item.storageLocationID,
          isNew: false,
        }));
        setStorageLocations(formatted);
      } else {
        setErrorT1("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Error fetching storage locations:", err);
      setErrorT1("Something went wrong. Please try again.");
    } finally {
      setLoadingT1(false);
    }
  };

  // ================= Table 2 — load =================
  const fetchPlantStorageLocationMappings = async () => {
    setErrorT2(null);
    setLoadingT2(true);
    try {
      const response = await axios.post(
        buildUrl(SLOC_ENDPOINTS.table2.load),
        {}
      );
      const data = response?.data?.data ?? response?.data;
      // Response shape (new contract):
      //   plantStorageLocationMappingList: [{
      //     plantStorageMappingID, plantID, storageLocationIDs:[...],
      //     productCountAtVendorLevel: [{ vendorMosymphonyID, vendorName, productCount }]
      //   }]
      // productCountAtVendorLevel is the single source of truth for the
      // Associated Products column (A6) and the Delete-Plant drawer rows (A7) —
      // both used to require separate count endpoints.
      const list =
        data?.plantStorageLocationMappingList ||
        data?.plantStorageLocationMappings;
      // The backend may return storageLocationIDs as an ARRAY OR as a
      // comma-separated STRING (e.g. "SLOC-TEST-1" or "0002, 0006").
      // Normalize to an array so the rest of the UI works the same way.
      const toSlocArray = (v) => {
        if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
        if (typeof v === "string" && v.trim()) {
          return v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        return [];
      };
      if (Array.isArray(list)) {
        const formatted = list.map((item) => {
          const slocs = toSlocArray(item.storageLocationIDs);
          return {
            plantStorageMappingID: item.plantStorageMappingID,
            plantID: item.plantID,
            storageLocationIDs: [...slocs],
            // snapshot of persisted associations to revert on Cancel
            originalStorageLocationIDs: [...slocs],
            productCountAtVendorLevel: Array.isArray(item.productCountAtVendorLevel)
              ? item.productCountAtVendorLevel
              : [],
            isNew: false,
          };
        });
        setPlantRows(formatted);

        // Derive Associated Products counts from the embedded per-CMO list.
        // A6 column shows the sum across all CMOs for each plant.
        const counts = {};
        formatted.forEach((row) => {
          const sum = (row.productCountAtVendorLevel || []).reduce(
            (acc, c) => acc + Number(c?.productCount || 0),
            0
          );
          counts[String(row.plantID)] = sum;
        });
        setProductCounts(counts);
      } else {
        setErrorT2("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Error fetching plant-SLOC mappings:", err);
      setErrorT2("Something went wrong. Please try again.");
    } finally {
      setLoadingT2(false);
    }
  };

  // BCM-234 A6: Associated Products counts are no longer fetched separately.
  // They are derived from `productCountAtVendorLevel` embedded in the response
  // of fetchPlantStorageLocationMappings (see derivation above).

  useEffect(() => {
    fetchStorageLocations();
    fetchPlantStorageLocationMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================= Table 1 — add / delete =================
  const handleAddStorageLocation = () => {
    setStorageLocations((prev) => [
      ...prev,
      { storageLocationID: "", isNew: true, rowError: "" },
    ]);
  };

  const handleStorageLocationChange = (index, value) => {
    setStorageLocations((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, storageLocationID: value, rowError: "" } : row
      )
    );
  };

  const handleCancelStorageLocation = (index) => {
    setStorageLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveStorageLocation = async (index) => {
    const row = storageLocations[index];
    // Block empty/whitespace SLOC IDs client-side (Req 11.1)
    const slocId = (row.storageLocationID || "").trim();
    if (isBlankSloc(row.storageLocationID)) {
      setStorageLocations((prev) =>
        prev.map((r, i) =>
          i === index
            ? { ...r, rowError: "Storage Location ID cannot be empty." }
            : r
        )
      );
      return;
    }
    if (savingT1[index]) return;
    setSavingT1((prev) => ({ ...prev, [index]: true }));
    try {
      const payload = {
        storageLocationMasterList: [{ storageLocationID: slocId }],
      };
      const response = await axios.post(
        buildUrl(SLOC_ENDPOINTS.table1.add),
        payload
      );
      const data = response?.data?.data ?? response?.data;
      if (data?.status === true || data?.status === "true") {
        await fetchStorageLocations();
      } else {
        // Surface backend 400 errors inline (Req 11.1)
        const message =
          data?.errorMessage ||
          data?.message ||
          "Failed to add Storage Location.";
        setStorageLocations((prev) =>
          prev.map((r, i) => (i === index ? { ...r, rowError: message } : r))
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.data?.errorMessage ||
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        "Failed to add Storage Location.";
      setStorageLocations((prev) =>
        prev.map((r, i) => (i === index ? { ...r, rowError: message } : r))
      );
    } finally {
      setSavingT1((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDeleteStorageLocation = async (row) => {
    const plan = planSlocDelete(row);
    if (plan.localRemove) {
      setStorageLocations((prev) => prev.filter((item) => item !== row));
      return;
    }
    try {
      const payload = {
        storageLocationMasterList: [plan.write.record],
      };
      const response = await axios.post(
        buildUrl(plan.write.endpoint),
        payload
      );
      const data = response?.data?.data ?? response?.data;
      if (data?.status === true || data?.status === "true") {
        // Client-side cascade: remove the deleted SLOC from every Table 2 row
        // in the UI before the next save is sent (Req 13.1 / Property 7).
        setPlantRows((prev) =>
          cascadeRemoveSloc(prev, row.storageLocationID)
        );
        await fetchStorageLocations();
        // Backend cascade is the source of truth; refetch mappings to reflect it.
        await fetchPlantStorageLocationMappings();
      } else {
        setErrorT1(
          data?.errorMessage ||
            data?.message ||
            "Failed to delete Storage Location."
        );
      }
    } catch (err) {
      console.error("Error deleting storage location:", err);
      // Refetch on save failure to restore consistent state.
      await fetchStorageLocations();
    }
  };

  // ================= Table 2 — add / edit / delete =================
  const handleAddPlantRow = () => {
    setPlantRows((prev) => [
      ...prev,
      {
        plantID: "",
        storageLocationIDs: [],
        originalStorageLocationIDs: [],
        isNew: true,
        rowError: "",
      },
    ]);
  };

  const handlePlantIdChange = (index, value) => {
    setPlantRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, plantID: value, rowError: "" } : row
      )
    );
  };

  const handleSlocSelectionChange = (index, selectedIds) => {
    setPlantRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, storageLocationIDs: selectedIds } : row
      )
    );
  };

  // Cancel during plant-row editing:
  //   - new (unsaved) row → drop it (nothing persisted yet)
  //   - existing row     → revert SLOC selection to the last persisted snapshot
  //                        and clear any inline row error; do NOT drop the row
  const handleCancelPlantRow = (index) => {
    setPlantRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      if (row.isNew) {
        return prev.filter((_, i) => i !== index);
      }
      return prev.map((r, i) =>
        i === index
          ? {
              ...r,
              storageLocationIDs: [...(r.originalStorageLocationIDs || [])],
              rowError: "",
            }
          : r
      );
    });
    setEditingIndex(null);
  };

  const toggleEditPlantRow = (index) => {
    setEditingIndex((prev) => (prev === index ? null : index));
  };

  // Save a plant row.
  // Contract is state-based (not diff-based): one call carrying the FULL current
  // set of storageLocationIDs for the plant. The backend reconciles the diff.
  //   new plant     -> POST addPlantStorageLocationMapping  { plantID, storageLocationIDs }
  //   existing plant-> POST updatePlantStorageLocationMapping
  //                    { plantStorageMappingID, plantID, storageLocationIDs }
  const handleSavePlantRow = async (index) => {
    const row = plantRows[index];
    const plantID = (row.plantID || "").trim();
    if (!plantID) {
      setPlantRows((prev) =>
        prev.map((r, i) =>
          i === index ? { ...r, rowError: "Plant ID cannot be empty." } : r
        )
      );
      return;
    }
    if (savingT2[index]) return;
    setSavingT2((prev) => ({ ...prev, [index]: true }));

    const isNewRow = row.isNew === true;
    const endpoint = isNewRow
      ? SLOC_ENDPOINTS.table2.add
      : SLOC_ENDPOINTS.table2.update;
    const record = isNewRow
      ? { plantID, storageLocationIDs: [...(row.storageLocationIDs || [])] }
      : {
          plantStorageMappingID: row.plantStorageMappingID,
          plantID,
          storageLocationIDs: [...(row.storageLocationIDs || [])],
        };

    try {
      await axios.post(buildUrl(endpoint), {
        plantStorageLocationMappingList: [record],
      });
      await fetchPlantStorageLocationMappings();
      setEditingIndex(null);
    } catch (err) {
      const message =
        err?.response?.data?.data?.errorMessage ||
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        "Failed to save plant mapping.";
      setPlantRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, rowError: message } : r))
      );
      // Refetch on save failure so the UI shows the last persisted state.
      await fetchPlantStorageLocationMappings();
    } finally {
      setSavingT2((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDeletePlantRow = (index) => {
    const row = plantRows[index];
    if (row.isNew) {
      setPlantRows((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    // BCM-234 A7: deleting a persisted plant must first reassign its active products
    // to an alternate plant per CMO. Open the Delete-Plant drawer (which performs the
    // atomic reassign + soft-delete via deletePlantAndReassignToProducts) rather than
    // soft-deleting the plant's SLOC mappings directly.
    setDeletePlantTarget(row.plantID);
  };

  const renderSlocMultiSelect = (row, index) => (
    <Select
      multiple
      displayEmpty
      value={row.storageLocationIDs || []}
      onChange={(e) => handleSlocSelectionChange(index, e.target.value)}
      renderValue={(selected) =>
        selected && selected.length > 0
          ? selected.join(", ")
          : "Select from the list"
      }
      style={{ width: 250 }}
      variant="outlined"
    >
      {slocOptions.map((option) => (
        <MenuItem key={option} value={option}>
          <Checkbox
            checked={(row.storageLocationIDs || []).indexOf(option) > -1}
          />
          <ListItemText primary={option} />
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <div className={classes.container}>
      {/* ===================== Table 1 ===================== */}
      <div>
        <Typography className={classes.sectionTitle}>
          Storage Location IDs
        </Typography>
        <TableContainer
          component={Paper}
          className={`${classes.table} default-table-styles`}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>
                  Storage Location ID
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingT1 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : errorT1 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <div className={classes.errorBox}>
                      <Typography>{errorT1}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                storageLocations.map((row, index) => (
                  <TableRow key={row.id || `new-sloc-${index}`}>
                    <TableCell className={classes.bodyCell}>
                      {row.isNew ? (
                        <div
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <TextField
                              placeholder="Type Storage Location ID"
                              value={row.storageLocationID}
                              onChange={(e) =>
                                handleStorageLocationChange(
                                  index,
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                const { value } = e.target;
                                if (
                                  typeof value === "string" &&
                                  value.trim() !== value
                                ) {
                                  handleStorageLocationChange(
                                    index,
                                    value.trim()
                                  );
                                }
                              }}
                              className={classes.textField}
                              variant="outlined"
                            />
                            {row.rowError && (
                              <span className={classes.rowError}>
                                {row.rowError}
                              </span>
                            )}
                          </div>
                          <div className={classes.inlineActions}>
                            <IconButton
                              onClick={() =>
                                handleCancelStorageLocation(index)
                              }
                              className={classes.iconBtn}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleSaveStorageLocation(index)}
                              className={classes.iconBtn}
                              disabled={
                                !row.storageLocationID?.trim() ||
                                savingT1[index]
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        row.storageLocationID
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {canEdit && (
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleDeleteStorageLocation(row)}
                        >
                          <DeleteIcon className={classes.deleteIcon} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {canEdit && (
          <Button
            variant="outlined"
            onClick={handleAddStorageLocation}
            className={classes.addButton}
          >
            <span className={classes.addIconCircle}>+</span>
            Add New Storage Location ID
          </Button>
        )}
      </div>

      {/* ===================== Table 2 ===================== */}
      <div>
        <Typography className={classes.sectionTitle}>
          Plant - Associated SLOCs
        </Typography>
        <TableContainer
          component={Paper}
          className={`${classes.table} default-table-styles`}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Plant ID</TableCell>
                <TableCell className={classes.headerCell}>
                  Associated Storage Location IDs
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Associated Products
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingT2 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : errorT2 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className={classes.errorBox}>
                      <Typography>{errorT2}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                plantRows.map((row, index) => {
                  const isEditing = row.isNew || editingIndex === index;
                  return (
                    <TableRow key={row.isNew ? `new-plant-${index}` : row.plantID}>
                      <TableCell className={classes.bodyCell}>
                        {row.isNew ? (
                          <TextField
                            placeholder="Enter Plant ID"
                            value={row.plantID}
                            onChange={(e) =>
                              handlePlantIdChange(index, e.target.value)
                            }
                            onBlur={(e) => {
                              const { value } = e.target;
                              if (
                                typeof value === "string" &&
                                value.trim() !== value
                              ) {
                                handlePlantIdChange(index, value.trim());
                              }
                            }}
                            className={classes.textField}
                            variant="outlined"
                          />
                        ) : (
                          row.plantID
                        )}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {isEditing ? (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {renderSlocMultiSelect(row, index)}
                            <div className={classes.inlineActions}>
                              <IconButton
                                onClick={() => handleCancelPlantRow(index)}
                                className={classes.iconBtn}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleSavePlantRow(index)}
                                className={classes.iconBtn}
                                disabled={
                                  !row.plantID?.trim() || savingT2[index]
                                }
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </div>
                        ) : (
                          <Typography component="span">
                            {(row.storageLocationIDs || []).join(", ")}
                          </Typography>
                        )}
                        {row.rowError && (
                          <div className={classes.rowError}>{row.rowError}</div>
                        )}
                      </TableCell>
                      {/* BCM-234 A6: Associated Products count (across all CMOs).
                          When count>0, render a blue clickable deep-link that opens the
                          GLOBAL (no-CMO) Product Details dashboard pre-filtered by this
                          Plant ID + Status=Active. */}
                      <TableCell className={classes.bodyCell}>
                        {(() => {
                          if (row.isNew) return "—";
                          const count =
                            productCounts[String(row.plantID)] || 0;
                          const label = `${count} Products`;
                          if (count > 0) {
                            return (
                              <button
                                type="button"
                                className={classes.productLink}
                                onClick={() => openProductsForPlant(row.plantID)}
                              >
                                {label}
                              </button>
                            );
                          }
                          return label;
                        })()}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {canEdit && !row.isNew && editingIndex !== index && (
                          <IconButton
                            onClick={() => toggleEditPlantRow(index)}
                            className={classes.iconBtn}
                          >
                            <EditIcon
                              fontSize="small"
                              className={classes.editIcon}
                            />
                          </IconButton>
                        )}
                        {canEdit && (
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleDeletePlantRow(index)}
                          >
                            <DeleteIcon className={classes.deleteIcon} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {canEdit && (
          <Button
            variant="outlined"
            onClick={handleAddPlantRow}
            className={classes.addButton}
          >
            <span className={classes.addIconCircle}>+</span>
            Add New Plant
          </Button>
        )}
        <Typography
          variant="caption"
          style={{ marginTop: 8, display: "block", color: "#777" }}
        >
          Plant rows: {tabBadgeCount(plantRows)}
        </Typography>
      </div>

      {/* BCM-234 A7: Delete Plant ID drawer — reassign products per CMO + delete.
          The drawer no longer fetches CMO/count/alternate data itself; it reads
          everything from the plant rows we already loaded:
            - the deleted plant's row → productCountAtVendorLevel + storageLocationIDs
            - all OTHER plants in plantRows → alternate-plant options */}
      <DeletePlantDrawer
        open={!!deletePlantTarget}
        plantID={deletePlantTarget}
        sponsorId={sponsorId}
        ecosystemId={ecosystemId}
        buildUrl={buildUrl}
        plantRows={plantRows}
        onClose={() => setDeletePlantTarget(null)}
        onDeleted={() => {
          fetchPlantStorageLocationMappings();
        }}
      />
    </div>
  );
};

export default PlantSLocTab;
